import Header from '@/components/elements/Header/Header';
import { getEmployeesWhoPerformServices } from '@/helpers';
import { bookingFlowTrackingSession } from '@/helpers/checkoutFlowTrackingSession';
import { useAppSelector } from '@/hooks';
import useMobileView from '@/hooks/useMobileView';
import { _s } from '@/locale';
import { BookState } from '@/types/state/book';
import { CampaignProduct, PlaceEmployee } from '@/types/state/shared';
import { useCallback, useMemo } from 'react';
import Modal from '../../redesign/Modal/Modal';
import { ScrollToTopElement } from '../BookAppointment.helpers';
import { useBookAppointmentContext } from '../BookAppointment.hooks';
import EmployeeItem from '../shared/EmployeeItem';
import Summary, { SummaryProps } from '../shared/Summary';

const baseTranslationKey = 'components.modules.modals.BookAppointment.views.PickEmployeeView';

const PickEmployeeView = () => {
  const { isMobileView } = useMobileView();
  const context = useBookAppointmentContext();
  const { place, services: pickedServices, campaigns } = useAppSelector((state) => state.book) as BookState;
  const hasPickedAddonServices = pickedServices.some((service) => service?.about?.settings?.isAddOn);
  const employeesWhoPerformServices: PlaceEmployee[] = getEmployeesWhoPerformServices(
    place.employees,
    pickedServices.map((service) => service.id),
  );

  const pickedProducts = useMemo(() => {
    if (!campaigns) return [];
    const products: CampaignProduct[] = [];

    campaigns.forEach((campaign) => {
      if (campaign.type === 7 && campaign.publicationType === 2 && campaign.products?.length > 0) {
        products.push(...campaign.products);
      }
    });
    return products;
  }, [campaigns]);

  const handleRemoveService = useCallback(
    (serviceId: number) => {
      context.onRemoveService(serviceId);
    },
    [context],
  );

  const summaryProps: SummaryProps = {
    pickedEmployee: undefined,
    pickedServices,
    pickedProducts,
    showAddAdditionalService: !hasPickedAddonServices,
    onRemoveService: handleRemoveService,
    onShowAvailableServices: handleShowAvailableServices,
    showPickedServices: true,
    showPickedProducts: true,
  };

  function handleShowAvailableServices() {
    context.updateView('pickServices');
    bookingFlowTrackingSession.addService('initiated');
  }

  const handleOnBack = () => {
    context.close();
    bookingFlowTrackingSession.cancelSession();
  };

  return (
    <Modal.Content floating={!isMobileView}>
      <ScrollToTopElement />
      <Modal.Header title={_s(`${baseTranslationKey}.title`)} onBack={handleOnBack} />
      <div className={`pb-lg flex flex-col ${isMobileView ? 'px-lg' : ''}`}>
        <div className="p-md">
          <Summary {...summaryProps} />
        </div>
        <div className="pt-xxl">
          <Header label={_s(`${baseTranslationKey}.title`)} size="md" />
        </div>
        <ul>
          {employeesWhoPerformServices?.length > 1 && <EmployeeItem onSelect={context.onPickEmployee} underline />}
          {employeesWhoPerformServices.map((employee, idx) => {
            return (
              <EmployeeItem
                key={idx}
                employee={employee}
                onSelect={context.onPickEmployee}
                underline={employeesWhoPerformServices.length - 1 !== idx}
              />
            );
          })}
        </ul>
      </div>
    </Modal.Content>
  );
};

export default PickEmployeeView;
